import { Bank, CheckCircle, CreditCard, DeviceMobile, Wallet } from "@phosphor-icons/react";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@reactive-resume/ui";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Link } from "react-router";

import { paymentService } from "@/client/services/payment";
import { useAuthStore } from "@/client/stores/auth";

const PLAN_FEATURES: Record<string, string[]> = {
  one_time: ["Download once", "PDF format", "ATS-Optimized", "Instant access after payment"],
  subscription: [
    "Unlimited downloads",
    "Access to all templates",
    "Edit anytime",
    "Valid for 30 days",
  ],
};

const PAYMENT_METHODS = [
  { name: "M-Pesa", Icon: DeviceMobile },
  { name: "Visa", Icon: CreditCard },
  { name: "Mastercard", Icon: CreditCard },
  { name: "Airtel Money", Icon: Wallet },
  { name: "Bank Transfer", Icon: Bank },
];

export const PricingSection = () => {
  const isLoggedIn = useAuthStore((state) => !!state.user);

  const { data: apiPlans = [] } = useQuery({
    queryKey: ["payment-plans"],
    queryFn: () => paymentService.getPaymentPlans(),
  });

  const plans = [
    {
      type: "ONE_TIME_DOWNLOAD" as const,
      name: "One-Time CV Download",
      tagline: "Perfect for a single job application",
      price: apiPlans.find((p) => p.name === "one_time")?.price,
      currency: "KES",
      popular: true,
      features: PLAN_FEATURES.one_time,
      cta: isLoggedIn ? "Pay Securely" : "Get Started",
      link: isLoggedIn ? "/dashboard/resumes" : "/auth/register",
    },
    {
      type: "MONTHLY_SUBSCRIPTION" as const,
      name: "Monthly Unlimited Access",
      tagline: "For active job seekers applying to many roles",
      price: apiPlans.find((p) => p.name === "subscription")?.price,
      currency: "KES",
      suffix: "/month",
      badge: "Best Value",
      features: PLAN_FEATURES.subscription,
      cta: isLoggedIn ? "Subscribe Securely" : "Get Started",
      link: isLoggedIn ? "/dashboard/resumes" : "/auth/register",
    },
  ];

  return (
    <section id="pricing" className="relative bg-background py-24 sm:py-32">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-4xl font-bold tracking-tight text-dark-gray sm:text-5xl">
            Simple, Transparent Pricing
          </h2>
          <p className="mt-6 text-lg leading-8 text-medium-gray">
            Pay via M-Pesa, Visa, Mastercard, Airtel Money, or Bank Transfer.
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-5xl gap-8 md:grid-cols-2">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.type}
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                className={`relative h-full overflow-visible ${
                  plan.badge ? "border-2 border-accent shadow-lg" : "border border-border shadow-md"
                }`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-6 bg-accent text-accent-foreground">
                    Most Popular
                  </Badge>
                )}
                {plan.badge && (
                  <Badge className="absolute -top-3 left-6 bg-accent text-accent-foreground">
                    {plan.badge}
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4 flex items-baseline gap-1">
                    {plan.price === undefined ? (
                      <span className="h-10 w-36 animate-pulse rounded-md bg-muted" />
                    ) : (
                      <span className="text-4xl font-bold text-primary">
                        {plan.currency} {plan.price}
                      </span>
                    )}
                    {plan.suffix && <span className="text-lg text-medium-gray">{plan.suffix}</span>}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <Button
                    asChild
                    className={`w-full ${
                      plan.badge
                        ? "bg-accent text-accent-foreground hover:bg-accent/90"
                        : "bg-primary text-primary-foreground hover:bg-primary/90"
                    }`}
                    size="lg"
                  >
                    <Link to={plan.link}>{plan.cta}</Link>
                  </Button>
                  {plan.tagline && (
                    <p className="text-center text-xs text-muted-foreground">{plan.tagline}</p>
                  )}
                  <ul className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <CheckCircle className="mt-0.5 size-5 shrink-0 text-accent" weight="fill" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Accepted Payment Methods */}
        <div className="mx-auto mt-24 max-w-3xl">
          <hr className="mb-12 border-border" />
          <div className="text-center">
            <h3 className="text-2xl font-bold text-dark-gray">Accepted Payment Methods</h3>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-8">
              {PAYMENT_METHODS.map((method) => {
                const Icon = method.Icon;
                return (
                  <div key={method.name} className="flex flex-col items-center gap-2">
                    <div className="flex size-16 items-center justify-center rounded-lg bg-muted">
                      <Icon className="size-8 text-accent" weight="regular" />
                    </div>
                    <span className="text-sm text-medium-gray">{method.name}</span>
                  </div>
                );
              })}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Fast and secure payments powered by Pesapal.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
